import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
  type DroppableProvided,
} from "@hello-pangea/dnd";
import type { FormikProps } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared";
import { getLongestLabel, getTextWidth } from "senf-shared/utils/helpers";
import { tv, type VariantProps } from "tailwind-variants";
import { cn } from "../../../../util/classNameConstructor";
import ScrollBar from "../../../atoms/scrollBar/ScrollBar";
import {
  getItemStyle,
  useOnDragEnd,
} from "../../../atoms/sortableList/Sortable";
import Button, { type ButtonProps } from "../../../core/button/Button";
import Checkbox from "../../../core/checkbox/Checkbox";
import type {
  Question,
  QuestionOption,
  QuestionOptionsKey,
  QuestionValue,
  ValueTypes,
} from "../../../special/survey/survey.types";
import { ChoiceItem, useChoiceState } from "../components/ChoiceItemWrapper";
import { HandleItem } from "../components/HandleItem";
import OptimizedInvisibleInput from "../components/OptimizedInvisibleInput";
import { useOverflow, useRandomSorting } from "../hooks";
import { pageTypeConfig } from "../pageTypes";
import { isChecked, toggle, type useEditQuestion } from "../utils";

const MatrixStyles = tv({
  slots: {
    Outer: "flex w-full flex-col mb-8",
    Wrapper: "overflow-x-auto pb-6",
    Table: "inline-table w-full",
    Row: "relative mb-0 grid",
    Cell: "relative flex items-center border-e-2 border-e-transparent p-1",
    ToggleContainer:
      "flex h-full w-full items-center justify-center rounded-lg px-2 py-1 px-",
  },
  variants: {
    mode: {
      display: {
        Outer: "mode-display",
      },
      edit: {
        Outer: "mode-edit",
      },
    },
    isMobile: {
      true: {
        Wrapper: `
          [.mode-display_&]:w-[calc(var(--w-screen,100vw)-1rem)]
          [.mode-display_&]:pe-[1rem]
          [.mode-edit_&]:-me-[1rem]
          [.mode-edit_&]:pe-[1rem]
          `,
      },
      false: {
        Wrapper: `
          [.mode-display_&]:w-[calc(var(--w-screen,100vw)/2+360px-2.5rem)]
          [.mode-display_&]:pe-[calc(var(--w-screen,100vw)/2-360px+2.5rem)]
          [.has-image_.mode-display_&]:w-[calc(var(--w-screen,100vw)/2-2.5rem+1.25rem)]
          [.has-image_.mode-display_&]:pe-[max(calc(2.5rem),calc((var(--w-screen,100vw)/2-720px)+2.5rem))]
          [.mode-edit_&]:-me-[2.5rem]
          [.mode-edit_&]:pe-[2.5rem]
          `,
      },
    },
    background: {
      true: {
        Row: "odd:bg-surface-subdued even:bg-surface-default rounded-xl",
        Cell: "[:nth-child(odd)>&:first-child]:bg-surface-subdued [:nth-child(even)>&:first-child]:bg-surface-default first:z-50 first:rounded-s-xl ltr:first:shadow-[-10px_0_0_0px_white] rtl:first:shadow-[10px_0_0_0px_white]",
      },
    },
    overflowingStart: {
      true: {
        Cell: "first:border-e-border-default first:border-dashed",
      },
    },
    isDragging: {
      true: {
        Row: "absolute z-[9999999] shadow-lg",
        Cell: "!shadow-none",
      },
    },
    toggleDisabled: {
      true: {
        ToggleContainer: "cursor-not-allowed",
      },
      false: {
        ToggleContainer:
          "hover:bg-surface-hovered [&:hover_button]:text-border-highlight-default cursor-pointer",
      },
    },
    header: {
      true: {
        Row: "",
        Cell: "group/cell flex-col",
      },
      false: {
        Row: "group/cell",
        Cell: "start-0 first:sticky",
      },
    },
  },
  defaultVariants: {
    header: false,
    toggleDisabled: false,
  },
});

interface MatrixProps extends Question, VariantProps<typeof MatrixStyles> {
  // general props
  mode?: "edit" | "display";
  wrapperRef?: React.RefObject<HTMLDivElement>;
  // edit props
  addItem: ReturnType<typeof useEditQuestion>["addItem"];
  removeItem: ReturnType<typeof useEditQuestion>["removeItem"];
  updateQuestion: ReturnType<typeof useEditQuestion>["updateQuestion"];
  updateText: ReturnType<typeof useEditQuestion>["updateText"];
  // display props
  value: ValueTypes;
  onChange?: (props: Partial<QuestionValue>) => void;
  formik?: FormikProps<Record<string, any>>;
  isMobile?: boolean;
}
interface TableBodyProps extends MatrixProps {
  droppableProvided: DroppableProvided;
  type: "checkbox" | "radio";
  checked: MatrixProps["value"];
  setChecked: React.Dispatch<React.SetStateAction<MatrixProps["value"]>>;
}
interface TableRowProps
  extends Omit<TableBodyProps, "onChange" | "droppableProvided"> {
  provided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  row: QuestionOption;
  rowIndex: number;
}

interface CheckboxProps extends Omit<TableBodyProps, "droppableProvided"> {
  optionValue: string;
  optionLabel: string;
  columnValue: string;
  columnLabel: string;
  snapshot?: DraggableStateSnapshot;
}

const getGridTemplateColumns = (
  columns?: QuestionOption[],
  props?: MatrixProps,
) => {
  // const n = columns?.length || 0;
  const cols = columns
    ?.map(
      (column) =>
        `minmax(${Math.min(
          getTextWidth(`${column.value || "mmmm"}mm`, "normal 12pt Nunito") +
            10,
          240,
        )}px, 1fr)`,
    )
    .join(" ");
  return {
    gridTemplateColumns:
      props?.mode === "edit"
        ? `minmax(180px, 3fr) ${cols}`
        : `minmax(150px, 3fr) ${cols}`,
  };
};

const CheckBoxes = ({
  type,
  optionValue,
  optionLabel,
  columnValue,
  columnLabel,
  snapshot,
  ...props
}: CheckboxProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const handleClick = (optionValue: string, columnValue: string) => {
    if (props.mode === "edit") return;
    const newValue = toggle(
      props.checked?.[optionValue] as string[],
      columnValue,
      type,
    );
    props.setChecked((e) => ({ ...e, [optionValue]: newValue }));
    // ref.current?.scrollIntoView({
    //   behavior: "smooth",
    //   block: "nearest",
    //   inline: "center",
    // });
  };

  const checked = isChecked(props.checked?.[optionValue], columnValue, type);

  const disabled = !!(
    props.maxChoices &&
    props.multipleChoice &&
    props.value?.[optionValue]?.length >= props.maxChoices &&
    !checked
  );

  const { Cell, ToggleContainer } = MatrixStyles({
    background: true,
    isDragging: snapshot?.isDragging,
    toggleDisabled: disabled,
  });

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div
      ref={ref}
      onClick={() =>
        !disabled ? handleClick(optionValue, columnValue) : undefined
      }
      className={Cell({
        class: cn("text-center", props.mode === "edit" && "cursor-grab"),
      })}
    >
      <div
        className={ToggleContainer({
          class: props.mode === "edit" && "pointer-events-none",
        })}
        tabIndex={-1}
      >
        <Checkbox
          checked={checked}
          disabled={!!disabled}
          disabledNotice={t("survey:disabledMessages.radioMatrix.maxChoices", {
            count: props?.maxChoices,
          })}
          tabIndex={-1}
          type={type}
          dataCy={`checkbox${`_optionLabel:${optionLabel}_columnLabel:${columnLabel}`}`}
          className={"pointer-events-none"}
        />
      </div>
    </div>
  );
};

const IsDraggingContext = React.createContext<boolean>(false);

const TableHead = ({ columns, ...props }: Omit<MatrixProps, "onChange">) => {
  const { Row, Cell } = MatrixStyles({
    header: true,
  });
  const [active, setActive] = useState<string | null>(null);
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  return (
    <Droppable droppableId="thead" direction="horizontal">
      {(droppableProvided: DroppableProvided) => (
        <div
          className={Row()}
          style={getGridTemplateColumns(columns, props)}
          ref={droppableProvided.innerRef}
          {...droppableProvided.droppableProps}
          id="pageColumns"
        >
          <div className={Cell()} />
          {columns?.map(({ value, uid }, columnIndex) => (
            <Draggable
              draggableId={uid}
              index={columnIndex}
              key={uid}
              isDragDisabled={props.mode !== "edit"}
            >
              {(
                provided: DraggableProvided,
                snapshot: DraggableStateSnapshot,
              ) => (
                <div
                  className={Cell({ class: "justify-between px-3" })}
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  style={getItemStyle(provided, snapshot, "x")}
                  tabIndex={-1}
                >
                  <HandleItem
                    active={active === uid}
                    deletable={
                      columns.length >
                      (pageTypeConfig[props.qtype]?.defaults?.minColumns || 0)
                    }
                    handleDelete={() => props.removeItem(uid, "columns")}
                    mode={props.mode}
                    type="column"
                  />
                  <OptimizedInvisibleInput
                    id={uid}
                    className="text-center"
                    size="md"
                    value={translation?.[value] || value}
                    onChange={({ target }) =>
                      props.updateText(target.value, uid, "columns")
                    }
                    onFocus={() => setActive(uid)}
                    onBlur={() => setActive(null)}
                    placeholder={`${t("global:column")} ${columnIndex + 1}`}
                    mode={props.mode}
                  />
                </div>
              )}
            </Draggable>
          ))}
          {droppableProvided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

const TableRow = ({
  columns,
  row,
  provided,
  snapshot,
  ...props
}: TableRowProps) => {
  const overflow = useOverflow(props.wrapperRef);
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const { Row, Cell } = MatrixStyles({
    background: true,
    isDragging: snapshot?.isDragging,
    overflowingStart: overflow.start,
  });

  const [active, setActive] = useState<string | null>(null);

  return (
    <IsDraggingContext.Consumer>
      {() => (
        <div
          className={Row()}
          ref={provided?.innerRef}
          {...provided?.draggableProps}
          {...provided?.dragHandleProps}
          style={{
            ...getGridTemplateColumns(columns, props),
            ...getItemStyle(provided, snapshot, "y"),
          }}
          tabIndex={-1}
        >
          <div className={Cell({ class: "px-3 py-2" })}>
            <HandleItem
              active={active === row.uid}
              deletable={
                (props.options?.length || 0) >
                (pageTypeConfig[props.qtype]?.defaults?.minOptions || 0)
              }
              handleDelete={() => props.removeItem(row.uid, "options")}
              type="row"
              mode={props.mode}
            />
            <OptimizedInvisibleInput
              id={row.uid}
              size="md"
              value={translation?.[row?.value] || row.value}
              onChange={({ target }) => {
                console.log("updateText", target.value, row.uid);
                props.updateText(target.value, row.uid, "options");
              }}
              onBlur={() => setActive(null)}
              onFocus={() => setActive(row.uid)}
              placeholder={
                row.placeholder || `${t("global:row")} ${props.rowIndex + 1}`
              }
              tabIndex={0}
              mode={props.mode}
            />
          </div>
          {columns?.map(({ uid, value }, i: number) => (
            <CheckBoxes
              key={i}
              optionValue={row.uid}
              optionLabel={row.value?.replace(/\s+/g, "_").toLowerCase()}
              columnValue={uid}
              columnLabel={value?.replace(/\s+/g, "_").toLowerCase()}
              columns={columns}
              snapshot={snapshot}
              {...props}
            />
          ))}
        </div>
      )}
    </IsDraggingContext.Consumer>
  );
};

const TableBody = ({
  options,
  droppableProvided,
  ...props
}: TableBodyProps) => {
  return (
    <div
      ref={droppableProvided.innerRef}
      {...droppableProvided.droppableProps}
      id="pageOptions"
    >
      {options?.map((option, rowIndex: number) => (
        <Draggable
          draggableId={option.uid}
          index={rowIndex}
          key={option.uid}
          isDragDisabled={props.mode !== "edit"}
        >
          {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
            <TableRow
              options={options}
              row={option}
              provided={provided}
              snapshot={snapshot}
              rowIndex={rowIndex}
              {...props}
            />
          )}
        </Draggable>
      ))}
      {droppableProvided.placeholder}
    </div>
  );
};

const AddButton = (props: { mode: MatrixProps["mode"] } & ButtonProps) => {
  if (props.mode !== "edit") return null;
  return <Button leadingIcon="plus-md" size="md" {...props} />;
};

const Matrix = ({ onChange, value, ...props }: MatrixProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const overflow = useOverflow(ref);
  const [translation] = useGlobalState().translationsState;

  const { Outer, Wrapper, Table, Row, Cell } = MatrixStyles({
    isMobile: !!props.isMobile,
    mode: props.mode,
    overflowingStart: overflow.start,
    background: true,
  });
  const { t } = useTranslation();
  const type =
    props.qtype !== "radioMatrix" || props.multipleChoice
      ? "checkbox"
      : "radio";

  const addItem = (key: QuestionOptionsKey) => {
    props.addItem(key, { focus: true });
  };
  const removeItem = (uid: string, key: QuestionOptionsKey) => {
    props.removeItem(uid, key, { focus: true });
  };
  const handleDragStart = () => {
    ref.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  const options = useRandomSorting(props, "options", props.mode);
  const columns = props.columns || [];

  useEffect(() => {
    const scrollIntoView = (event) => {
      const el = (event.target as HTMLElement)?.closest("label");
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    };
    ref.current?.addEventListener("focusin", scrollIntoView);
    return () => {
      ref.current?.removeEventListener("focusin", scrollIntoView);
    };
  }, [overflow]);

  const gridTemplateColumns = useMemo(() => {
    // return `1fr repeat(${columns.length}, minmax(52px, 1fr))`;
    return `minmax(${getLongestLabel(options, (e) => e.value, { minWidth: 150 })}px, auto) repeat(${columns.length}, minmax(${getLongestLabel(columns, (e) => e.value, { minWidth: 52 })}px, 1fr))`;
  }, [columns]);

  return (
    <div className={cn(Outer())}>
      <AddButton
        text={t("global:column")}
        onClick={() => addItem("columns")}
        classNameWrapper="ms-auto"
        mode={props.mode}
        id="pageColumns-addButton"
      />
      {props.mode === "display" ? (
        <div
          className={Wrapper({ class: "grid" })}
          ref={ref}
          style={{
            gridTemplateColumns,
          }}
        >
          <div className="p-1 grid grid-cols-subgrid col-span-full">
            <span />
            {columns.map((e) => (
              // <div
              //   key={e.uid}
              //   className="py-2 h-32 pt-[100%] flex items-center justify-center relative"
              // >
              //   <span className="absolute body-sm -rotate-45 origin-bottom-left translate-x-1/2 -translate-y-full top-full max-w-44 w-screen">
              //     {e.value}
              //   </span>
              // </div>
              <span key={e.uid} className="self-end body-sm py-2 text-center">
                {translation?.[e.value] || e.value}
              </span>
            ))}
          </div>
          <div
            className={cn("grid grid-cols-subgrid col-span-full auto-rows-fr")}
          >
            {options.map((option) => {
              return (
                <div
                  // biome-ignore lint/a11y/useSemanticElements: cannot use semantic elements because of the matrix structure
                  role="group"
                  aria-labelledby={`group-${option.uid}`}
                  key={option.uid}
                  className={Row({
                    class: "grid grid-cols-subgrid col-span-full",
                  })}
                >
                  <span
                    key={option.uid}
                    id={`group-${option.uid}`}
                    className={Cell({ class: "px-3 body-sm" })}
                  >
                    {translation?.[option.value] || option.value}
                  </span>
                  {columns.map((e) => (
                    <ChoiceItem
                      key={e.uid}
                      column={e}
                      option={option}
                      question={props}
                      value={value}
                      onChange={onChange}
                      classNames={{ label: "mx-0" }}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className={Wrapper()} ref={ref}>
          <div className={Table()}>
            <DragDropContext
              onDragEnd={useOnDragEnd(columns, (items: QuestionOption[]) =>
                props.updateQuestion(items, "columns"),
              )}
            >
              <TableHead wrapperRef={ref} {...props} removeItem={removeItem} />
            </DragDropContext>

            <DragDropContext
              onDragEnd={useOnDragEnd(options, (items: QuestionOption[]) =>
                props.updateQuestion(items, "options"),
              )}
              onDragStart={handleDragStart}
            >
              <Droppable droppableId="tbody">
                {(droppableProvided: DroppableProvided) => (
                  <TableBody
                    droppableProvided={droppableProvided}
                    wrapperRef={ref}
                    {...props}
                    options={options}
                    type={type}
                    removeItem={removeItem}
                  />
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
      )}
      <AddButton
        text={t("global:row")}
        onClick={() => addItem("options")}
        mode={props.mode}
        classNameWrapper="me-auto"
        id="pageOptions-addButton"
      />

      {props.mode === "display" ? (
        <div className="sticky bottom-0 h-0 z-[99999]">
          <ScrollBar referenceContainer={ref} />
        </div>
      ) : null}
    </div>
  );
};
Matrix.displayName = "Matrix";
export default Matrix;
